import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEmailTypeAvailableRolesQuery } from "@/modules/email-types/hooks/useEmailTypeAvailableRolesQuery";
import { useUpdateEmailTypeRolesMutation } from "@/modules/email-types/hooks/useUpdateEmailTypeRolesMutation";
import { displayEmailTypeRole } from "@/schemas/emailType";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AsyncButton from "@/components/AsyncButton";

interface EmailTypeEditorRolesDialogProps {
  emailTypeId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const Content = ({
  emailTypeId,
  isOpen,
  onOpenChange,
}: EmailTypeEditorRolesDialogProps) => {
  const { t } = useTranslation();
  const { store } = useRowSelectionContext();
  const { query, options } = useEmailTypeAvailableRolesQuery({
    emailTypeId,
    enabled: isOpen,
  });
  const updateRolesMutation = useUpdateEmailTypeRolesMutation(emailTypeId);

  useEffect(() => {
    if (!isOpen) {
      store.clear();
    }
  }, [isOpen, store]);

  const handleSubmit = useCallback(() => {
    const selectedRoleIds = Array.from(store.getSelection());
    if (selectedRoleIds.length === 0) {
      return;
    }

    updateRolesMutation.mutate(
      { add: selectedRoleIds },
      {
        onSuccess: () => {
          store.clear();
          onOpenChange(false);
        },
      },
    );
  }, [store, updateRolesMutation, onOpenChange]);

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: true,
      enableSelectAll: true,
      enableFilters: true,
      enableSorting: true,
    }),
    [],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90dvw] h-[95dvh]"
        data-testid="email-types-add-roles-dialog"
      >
        <DataTableQueryContextProvider value={{ query }}>
          <QueryTable
            schema={displayEmailTypeRole}
            name="emailTypeAvailableRoles"
            translationKey="emailTypes"
            query={query}
            options={options}
            tableFeatures={tableFeatures}
          >
            <div className="flex flex-col gap-3 min-h-0 min-w-0">
              <DialogHeader>
                <DialogTitle>
                  {t("emailTypes.roles.addDialogTitle")}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-row items-center gap-2">
                <QueryTable.Menubar className="m-0 grow">
                  <QueryTable.Menubar.Tools inDialog />
                  <QueryTable.Menubar.Columns inDialog />
                  <QueryTable.Menubar.Settings inDialog />
                </QueryTable.Menubar>
              </div>
              <div className="flex grow flex-row gap-2 overflow-hidden">
                <QueryTable.Content
                  className="flex-grow overflow-x-auto"
                  isInDialog
                >
                  <QueryTable.Status>
                    <QueryTable.SelectedRowsStatus />
                  </QueryTable.Status>
                </QueryTable.Content>
              </div>
              <DialogFooter className="gap-3">
                <DialogClose asChild>
                  <Button
                    variant="link"
                    disabled={updateRolesMutation.isPending}
                  >
                    {t("common.cancel")}
                  </Button>
                </DialogClose>
                <AsyncButton
                  type="button"
                  data-testid="email-types-submit-add-roles-button"
                  onClick={handleSubmit}
                  disabled={updateRolesMutation.isPending}
                  label={t("emailTypes.roles.addButton")}
                  loadingLabel={t("emailTypes.roles.addButtonLoading")}
                  loading={updateRolesMutation.isPending}
                />
              </DialogFooter>
            </div>
          </QueryTable>
        </DataTableQueryContextProvider>
      </DialogContent>
    </Dialog>
  );
};

export const EmailTypeEditorRolesDialog = (
  props: EmailTypeEditorRolesDialogProps,
) => (
  <RowSelectionProvider>
    <Content {...props} />
  </RowSelectionProvider>
);
